import React, { useCallback, useContext, useEffect, useState } from 'react'
import {
  MenuItem,
  Select,
  Grid,
  FormControl,
  FormControlLabel,
  InputLabel,
  Checkbox,
  ListItemText,
} from '@material-ui/core'
import FlexView from 'react-flexview/lib'
import { Controller, useForm } from 'react-hook-form'
import { SessionContextStore } from '../../../../context/SessionContext'
import {
  FirebaseConfigKey,
  FirebaseService,
} from '../../../../services/FirebaseService'
import {
  ModificationContextStore,
  POLICY_MODIFICATION_CONTEXT_ACTIONS,
} from '../../../../context/ModificationContext'
import SimpleAccordion from '../../../Core/Expansion/SimpleAccordion'
import { Action } from '../../../../models/Action'

export const ModifyPilotCertificationRating = () => {
  const TRAINING = [
    { key: 'InstrumentRating', value: 'Instrument rating' },
    { key: 'AircraftSingleEngineLand', value: 'Aircraft single engine land' },
    { key: 'AircraftMultiEngineLand', value: 'Aircraft multi-engine land' },
    { key: 'AircraftSingleEngineSea', value: 'Aircraft single engine sea' },
    { key: 'Glider', value: 'Glider' },
    { key: 'CertifiedFlightInstructor', value: 'Certified flight instructor' },
    {
      key: 'MasterCertifiedFlightInstructor',
      value: 'Master certified flight instructor',
    },
  ]
  const sessionContext = React.useContext(SessionContextStore)
  const modificationContext = React.useContext(ModificationContextStore)
  const [isOpen, setIsOpen] = useState(false)
  const {
    insurance,
    subscription,
    changePilotCertificate,
    changePilotRatings,
  } = useContext(ModificationContextStore)
  const [pilot_certification, setPilotCertification] = React.useState(
    subscription.insuredPerson.pilotCertification
  )
  const [pilot_training, setPilotTraining] = React.useState(
    subscription.insuredPerson.pilotTraining
  )
  const [error, setError] = React.useState()

  const notAStudent =
    subscription.insuredPerson.pilotCertification !== 'Student'

  const {
    register,
    control,
    getValues,
    trigger,
    errors,
    watch,
    reset,
    setValue,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      pilot_certification: subscription.insuredPerson.pilotCertification,
      pilot_training: subscription.insuredPerson.pilotTraining,
    },
  })

  React.useEffect(() => {
    let violations =
      pilot_certification !== 'Student' && pilot_training.length <= 0
    if (violations) {
      setError('You must choose pilot ratings')
      modificationContext.dispatch(
        new Action(
          POLICY_MODIFICATION_CONTEXT_ACTIONS.SET_IS_CERTIFICATION_VALID,
          false
        )
      )
    } else {
      setError('')
      modificationContext.dispatch(
        new Action(
          POLICY_MODIFICATION_CONTEXT_ACTIONS.SET_IS_CERTIFICATION_VALID,
          true
        )
      )
    }
  }, [
    subscription.insuredPerson.pilotCertification,
    subscription.insuredPerson.pilotTraining,
  ])

  React.useEffect(() => {
    changePilotCertificate(pilot_certification)
    changePilotRatings(pilot_training)
  }, [pilot_certification, pilot_training])

  return (
    <SimpleAccordion
      onClick={() => setIsOpen(!isOpen)}
      isOpen={isOpen}
      className="margin-top-large"
      title={
        <FlexView column className="space-between full-width">
          <div>Pilot Certification </div>
        </FlexView>
      }
      content={
        <>
          <Grid item sm="4" xs={12}>
            <Controller
              rules={{
                required: true,
                validate: (x) => !!x && x != 'default',
              }}
              control={control}
              defaultValue={'default'}
              name="pilot_certification"
              render={(
                { onChange, onBlur, value, name, ref },
                { invalid, isTouched, isDirty }
              ) => (
                <>
                  <Select
                    error={!!errors.pilot_certification}
                    fullWidth
                    variant="outlined"
                    onChange={onChange}
                    value={value}>
                    <MenuItem disabled value={'default'}>
                      <span className="gray">Pilot certification</span>
                    </MenuItem>
                    {sessionContext.user.insuranceType !== 'CFI' &&
                      subscription.insuredPerson.isStudent && (
                        <MenuItem
                          onClick={() => {
                            setPilotCertification('Student')
                          }}
                          value={'Student'}>
                          Student
                        </MenuItem>
                      )}
                    {sessionContext.user.insuranceType !== 'CFI' && (
                      <MenuItem
                        onClick={() => {
                          setPilotCertification('Recreational')
                        }}
                        value={'Recreational'}>
                        Recreational
                      </MenuItem>
                    )}
                    {sessionContext.user.insuranceType !== 'CFI' && (
                      <MenuItem
                        onClick={() => {
                          setPilotCertification('Sport')
                        }}
                        value={'Sport'}>
                        Sport
                      </MenuItem>
                    )}
                    {sessionContext.user.insuranceType !== 'CFI' && (
                      <MenuItem
                        onClick={() => {
                          setPilotCertification('Private')
                        }}
                        value={'Private'}>
                        Private
                      </MenuItem>
                    )}
                    <MenuItem
                      onClick={() => {
                        setPilotCertification('Commercial')
                      }}
                      value={'Commercial'}>
                      Commercial
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setPilotCertification('AirlineTransportPilot')
                      }}
                      value={'AirlineTransportPilot'}>
                      Airline transport pilot
                    </MenuItem>
                  </Select>
                  {!!errors.pilot_certification && (
                    <div className="text-tiny margin-top-3 margin-left-6 red">
                      Required
                    </div>
                  )}
                </>
              )}
            />
          </Grid>

          <Grid item xs={12} md={4} style={{ marginLeft: '16px' }}>
            {pilot_certification != 'Student' && (
              <Controller
                control={control}
                rules={{
                  required: true,
                  validate: (x) =>
                    !!x &&
                    (x.length > 0 || pilot_training.length > 0) &&
                    ((sessionContext.user.insuranceType === 'CFI' &&
                      pilot_training.some(
                        (value) =>
                          value === 'CertifiedFlightInstructor' ||
                          value === 'MasterCertifiedFlightInstructor'
                      )) ||
                      sessionContext.user.insuranceType !== 'CFI'),
                }}
                name="pilot_training"
                render={(
                  { onChange, onBlur, value, name, ref },
                  { invalid, isTouched, isDirty }
                ) => (
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel id="demo-simple-select-label">
                      Pilot rating
                    </InputLabel>
                    <Select
                      fullWidth
                      autoWidth
                      multiple
                      labelId="demo-simple-select-label"
                      variant="outlined"
                      value={pilot_training}
                      MenuProps={{ variant: 'menu' }}
                      onChange={onChange}
                      renderValue={(selected) => {
                        return !!selected
                          ? selected
                              .map(
                                (s) =>
                                  TRAINING.find((tr) => tr.key == s.trim())
                                    .value
                              )
                              .join(', ')
                          : ''
                      }}>
                      <MenuItem disabled value={'None'}>
                        <span className="gray">Pilot rating</span>
                      </MenuItem>
                      {TRAINING.map((train) => (
                        <MenuItem
                          key={train.key}
                          value={train.key}
                          onClick={(value) => {
                            if (
                              pilot_training.some(
                                (value) => value === train.key
                              )
                            ) {
                              setPilotTraining(
                                pilot_training.filter(
                                  (rating) => rating != train.key
                                )
                              )
                            } else {
                              setPilotTraining([...pilot_training, train.key])
                            }
                          }}>
                          <Checkbox
                            color="primary"
                            checked={
                              pilot_training
                                .map((x) => x.trim())
                                .indexOf(train.key.trim()) > -1 ||
                              (pilot_certification ===
                                'AirlineTransportPilot' &&
                                train.key === 'InstrumentRating' &&
                                pilot_training.some(
                                  (value) => value === 'InstrumentRating'
                                ))
                            }
                          />
                          <ListItemText primary={train.value} />
                        </MenuItem>
                      ))}
                    </Select>
                    {pilot_training.length <= 0 && (
                      <div className="text-tiny margin-top-3 margin-left-6 red">
                        Required
                      </div>
                    )}
                    {pilot_training.length > 0 &&
                      sessionContext.user.insuranceType === 'CFI' &&
                      !pilot_training.some(
                        (value) =>
                          value === 'CertifiedFlightInstructor' ||
                          value === 'MasterCertifiedFlightInstructor'
                      ) && (
                        <div className="text-tiny margin-top-3 margin-left-6 red">
                          CFI policies require CFI certification{' '}
                        </div>
                      )}
                  </FormControl>
                )}
              />
            )}
          </Grid>
        </>
      }
    />
  )
}
